// hooks/forms/useFormSubmission.ts
import { useCallback, useEffect, useRef } from 'react';
import {
    saveBookingCreatives,
    saveNewBooking,
    saveNewCampaign,
    saveNewCreative,
    saveNewSiteTag,
    updateBooking,
    updateCampaign,
    updateCreative,
    updateSiteTag,
} from '@/api';
import axios from 'axios';
import { formatDateForApi, notifyError } from '@/utils';

type ModalType = 'bookingModal' | 'creativeModal' | 'siteTagModal' | 'campaignModal' | 'impressionTagModal';

interface FormState {
    id?: number | null;
    customer_id?: number | null;
    start_date?: Date | string | null;
    end_date?: Date | string | null;
    booking_id?: number;
    creative_ids?: number[];
    [key: string]: unknown;
}

export const useFormSubmission = (
    refreshData: (overrideCustomerId?: number | null) => Promise<void>,
    handleCloseModal: (modalType: string) => void,
    selectedCustomerId: number | null
) => {
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const handleSave = useCallback(
        async (state: FormState, isValid: boolean, modalType: ModalType) => {
            if (!isValid) {
                console.warn('Form is invalid. Skipping save.');
                return;
            }

            try {
                abortControllerRef.current?.abort();
                abortControllerRef.current = new AbortController();
                const signal = abortControllerRef.current.signal;

                if (modalType === 'impressionTagModal') {
                    const { booking_id, creative_ids } = state;
                    await saveBookingCreatives(booking_id!, creative_ids!);
                } else {
                    type SaveFn = (data: Record<string, unknown>) => Promise<unknown>;
                    type UpdateFn = (data: Record<string, unknown>) => Promise<unknown>;

                    let saveFn: SaveFn | undefined;
                    let updateFn: UpdateFn | undefined;

                    switch (modalType) {
                        case 'bookingModal':
                            saveFn = saveNewBooking as unknown as SaveFn;
                            updateFn = updateBooking as unknown as UpdateFn;
                            break;
                        case 'creativeModal':
                            saveFn = saveNewCreative as unknown as SaveFn;
                            updateFn = updateCreative as unknown as UpdateFn;
                            break;
                        case 'siteTagModal':
                            saveFn = saveNewSiteTag as unknown as SaveFn;
                            updateFn = updateSiteTag as unknown as UpdateFn;
                            break;
                        case 'campaignModal':
                            saveFn = saveNewCampaign as unknown as SaveFn;
                            updateFn = updateCampaign as unknown as UpdateFn;
                            break;
                        default:
                            console.error(`No save function for modal: ${modalType}`);
                            return;
                    }

                    const { creative_ids: creativeIds, ...bookingFields } = state;

                    const formattedState: Record<string, unknown> = {
                        ...bookingFields,
                        customer_id: state.customer_id ?? selectedCustomerId,
                        start_date: formatDateForApi(state.start_date),
                        end_date: formatDateForApi(state.end_date),
                        signal,
                    };

                    let bookingId = state.id ?? null;

                    if (state.id) {
                        await updateFn!(formattedState);
                    } else {
                        const response = await saveFn!(formattedState);
                        if (modalType === 'bookingModal' && (response as Record<string, unknown>)?.booking_id) {
                            bookingId = (response as Record<string, unknown>).booking_id as number;
                        }
                    }

                    if (modalType === 'bookingModal' && creativeIds !== undefined && bookingId) {
                        await saveBookingCreatives(bookingId as number, creativeIds as number[]);
                    }
                }

                await refreshData();
                handleCloseModal(modalType);
            } catch (error) {
                if (!axios.isCancel(error)) {
                    console.error(`Error saving ${modalType}:`, error);
                    notifyError(`Failed to save ${modalType}. Please try again.`);
                }
            }
        },
        [handleCloseModal, refreshData, selectedCustomerId]
    );

    return { handleSave };
};
